/**
 * Rasterises one scan's annotated image for the PDF: the panorama with each finding's contour and numbered
 * badge drawn on top, in the image's own pixel space.
 *
 * WHY A CANVAS AND NOT THE LIVE VIEWER: the 360 viewer is WebGL with `preserveDrawingBuffer: false`, so
 * reading it back returns a black frame. This redraws from the stored `maskPoints` instead, which is also what
 * makes the output independent of wherever the user happened to have the camera pointed.
 *
 * REQUIRES CORS ON THE BLOB STORAGE. The panorama is served cross-origin; `<img>` renders it without CORS, but
 * reading pixels back does not. Without an `Access-Control-Allow-Origin` for the app origin the canvas is
 * tainted and `toDataURL` throws - which is exactly the SecurityError this module reports as a failed load.
 */
import { parseMaskPoints } from './findingsMetrics';
import { contourColor } from './reportFormat';

const MIN_POLYGON_POINTS = 3;

/** Source panoramas are ~20000 px wide. Cap the raster so the PDF stays a sane size; 2400 px still prints sharp. */
const MAX_RASTER_WIDTH = 2400;
/** JPEG rather than PNG: a photograph, and PNG would multiply the file size for no visible gain. */
const JPEG_QUALITY = 0.82;

const CONTOUR_LINE_WIDTH = 2;
const CONTOUR_FILL_ALPHA = 0.22;
/** Badge circle radius in raster pixels; sized to stay legible once the page scales the image down. */
const BADGE_RADIUS = 17;
const BADGE_FILL = '#c62828';
const BADGE_RING = '#ffffff';
const BADGE_FONT = '600 19px "IBM Plex Sans", Arial, sans-serif';

/**
 * The 360 viewer loads the same panorama through a plain <img> with no `crossOrigin`, and the browser caches
 * that response WITHOUT its CORS headers. A later `crossOrigin: 'anonymous'` request is served the cached
 * entry, fails the CORS check and errors - even when the storage account allows the origin. A unique query
 * parameter forces a separate cache entry. Azure ignores query parameters that are not part of the SAS.
 */
export function corsSafeUrl(url) {
  return `${url}${url.includes('?') ? '&' : '?'}_avaExport=${Date.now()}`;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Required for a readable canvas. Without it the draw succeeds and toDataURL throws instead.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The scan image could not be loaded for export.'));
    img.src = corsSafeUrl(url);
  });
}

/** '#ef8a17' -> 'rgba(239, 138, 23, 0.22)'. Canvas takes the string form; jsPDF does not, hence the split. */
function withCanvasAlpha(hex, alpha) {
  const value = String(hex).replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function drawArea(ctx, points, scale, stroke) {
  ctx.beginPath();
  points.forEach((p, i) => {
    const x = p.x * scale;
    const y = p.y * scale;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.fillStyle = withCanvasAlpha(stroke, CONTOUR_FILL_ALPHA);
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = CONTOUR_LINE_WIDTH;
  ctx.stroke();
}

function drawBadge(ctx, point, scale, label) {
  const x = point.x * scale;
  // Lift the badge clear of the contour, the same relationship the 360 viewer's `bottom center` anchor gives.
  const y = Math.max(BADGE_RADIUS + 2, point.y * scale - BADGE_RADIUS * 1.4);

  ctx.beginPath();
  ctx.arc(x, y, BADGE_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = BADGE_FILL;
  ctx.fill();
  ctx.strokeStyle = BADGE_RING;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.fillStyle = BADGE_RING;
  ctx.font = BADGE_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(label), x, y + 1);
}

/**
 * @param {string} imageUrl  the scan's panorama
 * @param {Array}  findings  that scan's findings, each carrying `maskPoints`, `polygonConfidence`, `badgeIndex`
 * @returns {Promise<{ dataUrl: string, width: number, height: number, drawn: number }>}
 * @throws  when the image cannot be loaded or the canvas cannot be read (see the CORS note above)
 */
export async function renderAnnotatedImage(imageUrl, findings = []) {
  const img = await loadImage(imageUrl);

  const scale = Math.min(1, MAX_RASTER_WIDTH / img.naturalWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);

  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  let drawn = 0;
  findings.forEach((finding) => {
    const points = parseMaskPoints(finding.maskPoints);
    if (points.length < MIN_POLYGON_POINTS) return;
    const stroke = contourColor(finding.polygonConfidence);
    drawArea(ctx, points, scale, stroke);
    drawn += 1;
  });

  // Badges last so no contour is ever drawn over a number.
  findings.forEach((finding) => {
    const points = parseMaskPoints(finding.maskPoints);
    if (points.length < MIN_POLYGON_POINTS) return;
    const top = points.reduce((a, b) => (b.y < a.y ? b : a));
    drawBadge(ctx, top, scale, finding.badgeIndex);
  });

  return {
    dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
    width: canvas.width,
    height: canvas.height,
    drawn,
  };
}

export default renderAnnotatedImage;
